import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Tag, AlertCircle, Instagram } from 'lucide-react';
import { getProducts, getCategories, listSocialEmbeds } from '../lib/api';
import { optimize } from '../lib/imageOptimizer';
import ProductImg from '../components/ProductImg';

// ── Horizontal Carousel ──
function ProductCarousel({ products, title, subtitle, linkText, linkHref }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  if (!products.length) return null;
  return (
    <section className="py-12 sm:py-16" data-testid={`section-${title?.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            {subtitle && <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF3B30] mb-1.5 font-body">{subtitle}</p>}
            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight uppercase text-zinc-900">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll(-1)} className="w-9 h-9 border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:border-zinc-400 transition-colors" data-testid="carousel-prev"><ChevronLeft size={16} /></button>
            <button onClick={() => scroll(1)} className="w-9 h-9 border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:border-zinc-400 transition-colors" data-testid="carousel-next"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
      <div ref={ref} className="flex gap-3 overflow-x-auto no-scrollbar pl-4 sm:pl-6 pr-4 sm:pr-6 max-w-[1400px] mx-auto">
        {products.map((p) => (
          <Link key={p.product_id} to={`/produit/${p.slug}`} className="product-card group shrink-0 w-[220px] sm:w-[260px] bg-white border border-zinc-100 hover:border-zinc-300 transition-all" data-testid={`card-${p.slug}`}>
            <div className="aspect-[3/4] overflow-hidden bg-zinc-50 relative">
              <ProductImg src={p.images?.[0]} alt={p.name} width={260} />
              {p.original_price && (
                <span className="absolute top-2 right-2 bg-[#22c55e] text-white text-[9px] font-bold px-2 py-0.5">-{Math.round((1 - p.price / p.original_price) * 100)}%</span>
              )}
            </div>
            <div className="p-3">
              <p className="text-[10px] text-zinc-400 font-body uppercase tracking-wider mb-0.5">{p.team}</p>
              <h3 className="text-xs font-medium text-zinc-900 font-body leading-tight mb-2 line-clamp-2">{p.name}</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[#FF3B30] font-body">{p.price?.toFixed(2)} €</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {linkText && (
        <div className="text-center mt-6">
          <Link to={linkHref} className="inline-flex items-center gap-2 border border-zinc-900 text-zinc-900 px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-all" data-testid="carousel-link">
            {linkText} <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </section>
  );
}

// ── League images — vraies photos liées aux ligues ──
const LEAGUE_IMAGES = [
  { name: 'Ligue 1', slug: 'Ligue 1', img: 'https://images.pexels.com/photos/30689706/pexels-photo-30689706.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { name: 'Premier League', slug: 'Premier League', img: 'https://images.pexels.com/photos/16062018/pexels-photo-16062018.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { name: 'La Liga', slug: 'La Liga', img: 'https://images.unsplash.com/photo-1575343406122-e374e9864206?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxMYSUyMExpZ2ElMjBzcGFuaXNoJTIwZm9vdGJhbGx8ZW58MHx8fHwxNzc2NjIyMTg0fDA&ixlib=rb-4.1.0&q=85' },
  { name: 'Serie A', slug: 'Serie A', img: 'https://images.unsplash.com/photo-1667375153077-072a085f32df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxTZXJpZSUyMEElMjBpdGFsaWFuJTIwZm9vdGJhbGx8ZW58MHx8fHwxNzc2NjIyMTg0fDA&ixlib=rb-4.1.0&q=85' },
  { name: 'Bundesliga', slug: 'Bundesliga', img: 'https://images.pexels.com/photos/27110090/pexels-photo-27110090.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { name: 'Sélections Nationales', slug: 'Sélections Nationales', img: 'https://images.pexels.com/photos/28847309/pexels-photo-28847309.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
];

// ── Main Component ──
export default function HomePage() {
  const [bestSellers, setBestSellers] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [wcProducts, setWcProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [socialEmbeds, setSocialEmbeds] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadError(false);
      try {
        const [bestRes, newRes, wcRes, catRes] = await Promise.all([
          getProducts({ sort: 'newest', limit: 12, light: true, withCount: false }),
          getProducts({ sort: 'price_desc', limit: 12, light: true, withCount: false }),
          getProducts({ subcategory: 'Sélections Nationales', limit: 12, light: true, withCount: false }),
          getCategories(),
        ]);
        setBestSellers(bestRes.data.products || []);
        setNewProducts(newRes.data.products || []);
        setWcProducts(wcRes.data.products || []);
        setCategories(catRes.data.categories || []);
      } catch (e) {
        console.error('[HomePage] Load failed:', e);
        setLoadError(true);
      }
      // Social embeds (non-blocking, errors silently ignored)
      try {
        const r = await listSocialEmbeds();
        setSocialEmbeds(r.embeds || []);
      } catch (e) {
        /* ignore */
      }
    };
    load();
  }, []);

  return (
    <div data-testid="home-page">

      {/* ══ HERO ══ */}
      <section className="relative min-h-[80vh] sm:min-h-[85vh] flex items-center overflow-hidden dark-region" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1768492263433-b81fdcdce0e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxmb290YmFsbCUyMGplcnNleSUyMHNwb3J0JTIwYXBwYXJlbHxlbnwwfHx8fDE3NzYwMzc4MjZ8MA&ixlib=rb-4.1.0&q=85"
            alt="One Maillot" referrerPolicy="no-referrer" loading="eager" decoding="async" fetchpriority="high"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 w-full py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400 mb-4 font-body animate-fade-in">Premium Sportswear</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-4 text-white animate-fade-in" style={{ animationDelay: '0.1s' }}>
              One Maillot,<br />One Passion
            </h1>
            <p className="text-zinc-300 font-body text-base sm:text-lg max-w-lg mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Les maillots les plus recherchés au monde. Collection 2026/27, répliques premium avec finitions haut de gamme.
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Link to="/catalogue" className="btn-primary inline-flex items-center gap-3 bg-white text-black font-bold text-sm px-8 py-4 uppercase tracking-wider" data-testid="hero-cta">
                Voir la collection <ArrowRight size={16} />
              </Link>
              <Link to="/catalogue?subcategory=S%C3%A9lections+Nationales" className="btn-outline inline-flex items-center gap-3 border-2 border-white text-white font-bold text-sm px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-black transition-all" data-testid="hero-cta-wc">
                Coupe du Monde 2026
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ Promo Banner ══ */}
      <section className="bg-[#FF3B30] py-3" data-testid="promo-banner">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-center gap-3">
          <Tag size={16} className="text-white shrink-0" />
          <p className="text-white font-heading text-sm sm:text-base font-bold uppercase tracking-wider text-center">
            2 maillots achetés = le 3ème offert
          </p>
          <Link to="/catalogue" className="bg-white text-[#FF3B30] text-[10px] font-bold uppercase tracking-wider px-3 py-1 shrink-0 hover:bg-zinc-100 transition-colors">
            En profiter
          </Link>
        </div>
      </section>

      {/* ══ Error Banner ══ */}
      {loadError && (
        <section className="bg-amber-50 border-y border-amber-200 py-4" data-testid="home-load-error">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-center gap-3 text-amber-900">
            <AlertCircle size={18} className="shrink-0" />
            <p className="text-sm">
              Le chargement des produits a échoué. <button onClick={() => window.location.reload()} className="underline font-bold ml-1">Réessayer</button>
            </p>
          </div>
        </section>
      )}

      {/* ══ Best Sellers Carousel ══ */}
      <ProductCarousel
        products={bestSellers}
        title="Derniers Arrivages"
        subtitle="Nouveautés"
        linkText="Voir tout le catalogue"
        linkHref="/catalogue"
      />

      {/* ══ Leagues Category Grid ══ */}
      <section className="py-12 sm:py-16 bg-zinc-50" data-testid="leagues-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF3B30] mb-1.5 font-body">Explorer</p>
            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight uppercase text-zinc-900">Nos Ligues & Championnats</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {LEAGUE_IMAGES.map(({ name, slug, img }) => (
              <Link key={slug} to={`/catalogue?subcategory=${encodeURIComponent(slug)}`}
                className="group relative aspect-[4/3] overflow-hidden bg-zinc-200" data-testid={`league-${slug}`}>
                <img src={img} alt={name} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-heading text-base sm:text-lg font-bold uppercase tracking-tight text-white">{name}</h3>
                </div>
              </Link>
            ))}
          </div>
          {/* Subcategory pills */}
          {categories[0]?.subcategories?.length > 0 && (
            <div className="flex gap-2 flex-wrap justify-center mt-6">
              {categories[0].subcategories.map((sub) => (
                <Link key={sub.slug} to={`/catalogue?subcategory=${encodeURIComponent(sub.name)}`}
                  className="border border-zinc-300 bg-white px-4 py-2 text-xs font-body text-zinc-500 hover:text-zinc-900 hover:border-zinc-900 transition-all">
                  {sub.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ Coupe du Monde 2026 Carousel ══ */}
      {wcProducts.length > 0 && (
        <ProductCarousel
          products={wcProducts}
          title="Coupe du Monde 2026"
          subtitle="Sélections Nationales"
          linkText="Voir toutes les sélections"
          linkHref="/catalogue?subcategory=S%C3%A9lections+Nationales"
        />
      )}

      {/* ══ Nouveautés Carousel ══ */}
      <div className="bg-zinc-50">
        <ProductCarousel
          products={newProducts}
          title="Notre Sélection"
          subtitle="Coups de coeur"
          linkText="Voir tout"
          linkHref="/catalogue"
        />
      </div>

      {/* ══ Social embeds grid (Admin → Vitrine sociale) ══ */}
      {socialEmbeds.length > 0 && (
        <section className="bg-black text-white pt-16 sm:pt-20 pb-4" data-testid="social-embeds-section">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <p className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-3 font-body">
                @onemaillot
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase">
                Dans le feed
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {socialEmbeds.map(e => (
                <a
                  key={e.id}
                  href={e.post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square overflow-hidden bg-zinc-900"
                  data-testid={`social-embed-${e.id}`}
                  aria-label={`${e.platform} — ${e.caption || 'Voir le post'}`}
                >
                  <img
                    src={e.thumbnail_url}
                    alt={e.caption || `${e.platform} post`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* dark gradient on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* platform badge */}
                  <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-white p-1.5 opacity-90">
                    {e.platform === 'instagram' && <Instagram size={14} />}
                    {e.platform === 'tiktok' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21Z" />
                      </svg>
                    )}
                    {e.platform === 'snapchat' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12.166 2c2.847.063 5.027 1.62 6.054 4.295.342.89.36 1.823.31 2.762-.028.512-.07 1.024-.106 1.535-.005.069.018.115.077.155.215.146.466.21.717.245.336.046.668.107.971.272.604.328.696 1.013.184 1.483-.245.225-.541.357-.85.456-.512.165-1.04.276-1.539.487-.595.252-.766.554-.586 1.176.336 1.16 1.034 2.073 2 2.793.514.383 1.083.66 1.71.798.345.077.515.227.523.443.011.305-.169.484-.402.62-.586.343-1.245.495-1.913.595-.213.032-.27.123-.31.328-.075.392-.198.776-.34 1.15-.13.34-.357.469-.706.43-.418-.046-.829-.144-1.246-.198-.553-.072-1.108-.07-1.656.057-.494.114-.928.336-1.317.66-.46.382-.95.713-1.522.918-1.247.448-2.487.43-3.708-.092-.55-.235-1.018-.587-1.461-.984-.45-.403-.967-.65-1.561-.74-.66-.1-1.319-.092-1.976.02-.262.045-.526.094-.79.119-.314.03-.518-.104-.64-.39-.158-.371-.297-.75-.382-1.146-.062-.286-.218-.366-.473-.4-.581-.077-1.157-.183-1.701-.41-.301-.126-.585-.275-.776-.561-.211-.317-.13-.65.221-.811.279-.127.581-.187.875-.272 1.176-.341 2.052-1.05 2.677-2.097.225-.377.405-.776.512-1.205.062-.246-.005-.39-.241-.498-.413-.187-.847-.32-1.273-.477-.293-.108-.585-.227-.847-.402-.51-.34-.485-.911.022-1.265.293-.205.629-.298.973-.36.305-.054.609-.106.866-.295.078-.057.106-.121.101-.215-.045-.726-.114-1.452-.122-2.18-.013-1.207.227-2.35.847-3.397 1.005-1.7 2.479-2.793 4.382-3.281.512-.131 1.04-.195 1.581-.222Z" />
                      </svg>
                    )}
                  </div>
                  {/* caption on hover */}
                  {e.caption && (
                    <p className="absolute bottom-2 left-2 right-2 text-[11px] text-white font-body line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {e.caption}
                    </p>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══ Follow us section ══ */}
      <section className="bg-black text-white py-20 sm:py-24" data-testid="follow-us-section">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 text-center">
          <p className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-3 font-body">
            Suis-nous
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-4">
            Rejoins la team<br />
            <span className="text-[#FF3B30]">One Maillot</span>
          </h2>
          <p className="text-zinc-400 font-body text-sm sm:text-base max-w-xl mx-auto mb-10">
            Les drops, les coulisses, les bons plans. Tout est sur nos réseaux.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {/* Instagram */}
            <a
              href="https://www.instagram.com/onemaillot"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all duration-300 px-5 sm:px-7 py-3.5 sm:py-4"
              data-testid="follow-instagram"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-zinc-700 font-body">Instagram</div>
                <div className="text-sm font-bold tracking-wide font-body">@onemaillot</div>
              </div>
            </a>

            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@onemaillot"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all duration-300 px-5 sm:px-7 py-3.5 sm:py-4"
              data-testid="follow-tiktok"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21Z" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-zinc-700 font-body">TikTok</div>
                <div className="text-sm font-bold tracking-wide font-body">@onemaillot</div>
              </div>
            </a>

            {/* Snapchat */}
            <a
              href="https://snapchat.com/t/EztmU7Q1"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all duration-300 px-5 sm:px-7 py-3.5 sm:py-4"
              data-testid="follow-snapchat"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.166 2c2.847.063 5.027 1.62 6.054 4.295.342.89.36 1.823.31 2.762-.028.512-.07 1.024-.106 1.535-.005.069.018.115.077.155.215.146.466.21.717.245.336.046.668.107.971.272.604.328.696 1.013.184 1.483-.245.225-.541.357-.85.456-.512.165-1.04.276-1.539.487-.595.252-.766.554-.586 1.176.336 1.16 1.034 2.073 2 2.793.514.383 1.083.66 1.71.798.345.077.515.227.523.443.011.305-.169.484-.402.62-.586.343-1.245.495-1.913.595-.213.032-.27.123-.31.328-.075.392-.198.776-.34 1.15-.13.34-.357.469-.706.43-.418-.046-.829-.144-1.246-.198-.553-.072-1.108-.07-1.656.057-.494.114-.928.336-1.317.66-.46.382-.95.713-1.522.918-1.247.448-2.487.43-3.708-.092-.55-.235-1.018-.587-1.461-.984-.45-.403-.967-.65-1.561-.74-.66-.1-1.319-.092-1.976.02-.262.045-.526.094-.79.119-.314.03-.518-.104-.64-.39-.158-.371-.297-.75-.382-1.146-.062-.286-.218-.366-.473-.4-.581-.077-1.157-.183-1.701-.41-.301-.126-.585-.275-.776-.561-.211-.317-.13-.65.221-.811.279-.127.581-.187.875-.272 1.176-.341 2.052-1.05 2.677-2.097.225-.377.405-.776.512-1.205.062-.246-.005-.39-.241-.498-.413-.187-.847-.32-1.273-.477-.293-.108-.585-.227-.847-.402-.51-.34-.485-.911.022-1.265.293-.205.629-.298.973-.36.305-.054.609-.106.866-.295.078-.057.106-.121.101-.215-.045-.726-.114-1.452-.122-2.18-.013-1.207.227-2.35.847-3.397 1.005-1.7 2.479-2.793 4.382-3.281.512-.131 1.04-.195 1.581-.222Z" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-zinc-700 font-body">Snapchat</div>
                <div className="text-sm font-bold tracking-wide font-body">@onemaillot</div>
              </div>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
